import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Copy, Check, ExternalLink, Globe, User, Handshake, ShieldCheck } from "lucide-react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

const GROUPS = [
  {
    key: "public",
    title: "Public",
    desc: "Anyone — no login required.",
    icon: Globe,
    tone: "text-zinc-600 bg-zinc-100",
    links: [
      { label: "Home", path: "/" },
      { label: "Login", path: "/login" },
      { label: "Sign up", path: "/signup" },
      { label: "Forgot password", path: "/forgot-password" },
      { label: "Free CV analysis (lead magnet)", path: "/analyse" },
      { label: "Affiliate landing / apply", path: "/partners" },
      { label: "Privacy policy", path: "/privacy" },
    ],
  },
  {
    key: "users",
    title: "Users (job seekers)",
    desc: "Requires a user login.",
    icon: User,
    tone: "text-[#24468C] bg-[#24468C]/10",
    links: [
      { label: "Job Matches", path: "/matches" },
      { label: "Quick Apply", path: "/apply" },
      { label: "Auto-Apply", path: "/auto-apply" },
      { label: "Profile", path: "/profile" },
      { label: "CVs", path: "/cvs" },
      { label: "Tailor CV", path: "/tailor" },
      { label: "Screening", path: "/screening" },
      { label: "Application Tracker", path: "/tracker" },
      { label: "Refer & Earn", path: "/referrals" },
      { label: "Billing / Subscription", path: "/billing" },
      { label: "Settings", path: "/settings" },
    ],
  },
  {
    key: "affiliates",
    title: "Affiliates",
    desc: "Partners who earn commission.",
    icon: Handshake,
    tone: "text-emerald-700 bg-emerald-50",
    links: [
      { label: "Apply to program (public)", path: "/partners" },
      { label: "Affiliate dashboard (login)", path: "/partners/dashboard" },
      { label: "Referral share link (format)", path: "/?ref=THEIR_CODE" },
    ],
  },
  {
    key: "admin",
    title: "Admin (you + staff)",
    desc: "Same /login as users, then the Admin console appears.",
    icon: ShieldCheck,
    tone: "text-[#8a6410] bg-[#E8A93C]/15",
    links: [
      { label: "Overview", path: "/admin" },
      { label: "Users", path: "/admin/users" },
      { label: "Subscriptions", path: "/admin/subscriptions" },
      { label: "Affiliates", path: "/admin/affiliates" },
      { label: "Referrals", path: "/admin/referrals" },
      { label: "CV Analysis Leads", path: "/admin/leads" },
      { label: "Job Matcher", path: "/admin/matcher" },
      { label: "LLM Settings", path: "/admin/llm" },
      { label: "LLM Usage", path: "/admin/llm-usage" },
      { label: "Email", path: "/admin/email" },
      { label: "GDPR / Data", path: "/admin/gdpr" },
      { label: "Audit Log", path: "/admin/audit" },
      { label: "Settings", path: "/admin/settings" },
    ],
  },
];

function LinkRow({ label, url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard blocked */ }
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-zinc-100 last:border-0" data-testid={`link-row-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{label}</p>
        <a href={url} target="_blank" rel="noreferrer" className="text-xs text-[#24468C] hover:underline break-all">{url}</a>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={copy} title="Copy URL" data-testid="copy-link-btn" className="p-2 rounded-md text-muted-foreground hover:bg-zinc-100 transition-colors">
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
        </button>
        <a href={url} target="_blank" rel="noreferrer" title="Open" className="p-2 rounded-md text-muted-foreground hover:bg-zinc-100 transition-colors">
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

export default function AdminLinks() {
  return (
    <div className="space-y-6" data-testid="admin-links">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Platform Links</h1>
        <p className="text-muted-foreground text-sm">Quick reference for every Jobsmith URL, grouped by audience. URLs use this environment ({ORIGIN}).</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {GROUPS.map((g) => {
          const Icon = g.icon;
          return (
            <Card key={g.key} data-testid={`link-group-${g.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2.5 mb-1">
                  <span className={`inline-flex items-center justify-center h-8 w-8 rounded-lg ${g.tone}`}><Icon className="h-4 w-4" strokeWidth={1.75} /></span>
                  <h2 className="font-heading font-semibold text-lg">{g.title}</h2>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{g.desc}</p>
                <div>
                  {g.links.map((l) => (
                    <LinkRow key={l.path} label={l.label} url={`${ORIGIN}${l.path}`} />
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card data-testid="link-notes">
        <CardContent className="p-5 text-sm text-muted-foreground space-y-2">
          <p><strong className="text-foreground">Admin login:</strong> there is no separate admin URL — admins sign in at <code>/login</code> like everyone else, then the Admin console becomes available.</p>
          <p><strong className="text-foreground">Adding staff admins:</strong> go to <a href={`${ORIGIN}/admin/users`} className="text-[#24468C] hover:underline">Users</a>, open the person's account, and set their role to <em>admin</em> (full) or <em>support</em> (limited). They log in with their own email/password.</p>
          <p><strong className="text-foreground">Affiliate links:</strong> each approved affiliate gets a unique <code>?ref=CODE</code> shown on their dashboard; sign-ups through it are attributed to them.</p>
        </CardContent>
      </Card>
    </div>
  );
}
